/**
 * A dónde iba el usuario cuando le mandamos a identificarse.
 *
 * Lo deja puesto quien le manda a /auth (el test de nivel, y desde el 16-10 cualquier ruta
 * protegida a la que se llega sin sesión: el botón del correo al entrenador, que abre la
 * ficha del cliente en Seguimiento) y lo consume el PublicRoute de App.js, justo al entrar.
 *
 * Por qué hace falta: el que llega de Instagram, hace el test y se registra acababa en
 * el panel sin haber visto un precio. AuthPage navega a /onboarding -> /planes, pero en
 * cuanto el registro guarda el token `isAuthenticated` pasa a true y PublicRoute -- que
 * envuelve /auth para echar de allí al que ya ha entrado -- redirige a /dashboard ANTES,
 * ganándole la carrera. El resultado del test seguía guardado, pero nunca llegaba a la
 * pantalla que lo usa. Se perdía la venta entera.
 *
 * Va en sessionStorage: es de esta visita, no para siempre, y sobrevive al registro
 * porque es la misma pestaña.
 */
export const DESTINO_TRAS_ENTRAR = 'destino_tras_entrar';

export const dejarDestino = (ruta) => {
    try { sessionStorage.setItem(DESTINO_TRAS_ENTRAR, ruta); } catch { /* modo privado */ }
};

/**
 * Lo lee SIN borrarlo, a propósito.
 *
 * Se consulta desde el render de PublicRoute, y ese render ocurre dos veces seguidas al
 * entrar. Si la lectura borrase, la primera vuelta mandaría a /planes y la segunda -- ya
 * sin destino -- mandaría al panel pisando a la primera, que es exactamente el fallo que
 * esto venía a arreglar. Leer es leer: el que llega al destino lo limpia (limpiarDestino).
 */
export const leerDestino = () => {
    try { return sessionStorage.getItem(DESTINO_TRAS_ENTRAR); } catch { return null; }
};

export const limpiarDestino = () => {
    try { sessionStorage.removeItem(DESTINO_TRAS_ENTRAR); } catch { /* modo privado */ }
};

/** El panel de cada uno: el del equipo para el equipo, el de cliente para el cliente. */
export const panelDe = (user) => (user?.role === 'admin' || user?.role === 'trainer' ? '/admin' : '/dashboard');

const esRutaInterna = (ruta) => typeof ruta === 'string' && ruta.length <= 500
    && ruta.startsWith('/') && !ruta.startsWith('//') && !ruta.includes('\\')
    && ![...ruta].some((c) => c.charCodeAt(0) < 32);

/**
 * A DÓNDE SE VA AL ENTRAR (16-10): a donde iba (`leerDestino`) o a su panel. Lo usan el
 * PublicRoute y AuthPage, que son los dos que navegan al entrar: si cada uno decidiera por
 * su lado, el último en navegar pisaría al otro.
 *
 * Solo a una ruta interna: lo que traiga esquema o servidor («https://...», «//otra.web»)
 * no se usa, como la vuelta de un pago en el servidor (`core/stripe_billing.build_frontend_url`).
 * Y al equipo, solo a su panel: un destino de cliente que quedara de antes (el test de
 * nivel) le llevaría a una pantalla que no es suya.
 */
export const destinoTrasEntrar = (user, pendiente = leerDestino()) => {
    const panel = panelDe(user);
    if (!esRutaInterna(pendiente)) return panel;
    if (panel === '/admin') {
        const ruta = pendiente.split(/[?#]/)[0];
        return ruta === panel || ruta.startsWith(`${panel}/`) ? pendiente : panel;
    }
    return pendiente;
};
